import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Typography,
  Grid,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { InsertDriveFile as FileIcon, Image as ImageIcon, Link as LinkIcon, CloudUpload as UploadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { fileToBase64, validateFileForBase64, isImage } from '../../utils/fileUtils';

const EventForm = ({ open, onClose, onSubmit, event = null, userId = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'personal',
    location: '',
    tags: [],
    eventDate: dayjs(),
    documentUrl: '',
    documentType: null,
    fileName: '',
    isAnniversary: false
  });

  const [tagInput, setTagInput] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadMethod, setUploadMethod] = useState(0); // 0 = URL, 1 = Upload
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (event) {
      const hasDocument = event.documentUrl || event.imageUrl;
      setFormData({
        title: event.title || '',
        description: event.description || '',
        category: event.category || 'personal',
        location: event.location || '',
        tags: event.tags || [],
        eventDate: event.eventDate ? dayjs(event.eventDate) : dayjs(),
        documentUrl: event.documentUrl || event.imageUrl || '',
        documentType: event.documentType || (event.imageUrl ? 'image' : null),
        fileName: event.fileName || '',
        isAnniversary: event.isAnniversary || false
      });
      setSelectedFile(null);
      setUploadMethod(hasDocument && !event.documentUrl?.startsWith('data:') ? 0 : 1);
      setPreviewUrl(event.documentUrl || event.imageUrl || '');
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'personal',
        location: '',
        tags: [],
        eventDate: dayjs(),
        documentUrl: '',
        documentType: null,
        fileName: '',
        isAnniversary: false
      });
      setSelectedFile(null);
      setUploadMethod(0);
      setPreviewUrl('');
    }
    setUploadError('');
  }, [event, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    const newTag = tagInput.trim();
    if (newTag) {
      // Case-insensitive check - don't add if tag already exists (case-insensitive)
      const tagLower = newTag.toLowerCase();
      const tagExists = formData.tags.some(tag => tag.toLowerCase() === tagLower);
      if (!tagExists) {
        handleChange('tags', [...formData.tags, newTag]);
        setTagInput('');
      } else {
        // Tag already exists, just clear input
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const validation = validateFileForBase64(file);
      
      if (!validation.valid) {
        setUploadError(validation.reason);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      try {
        // Convert to base64 for small images
        const base64 = await fileToBase64(file);
        setSelectedFile(file);
        setPreviewUrl(base64);
        setUploadError('');
      } catch (error) {
        setUploadError('Error processing file. Please try again.');
        setSelectedFile(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setFormData(prev => ({
      ...prev,
      documentUrl: '',
      documentType: null,
      fileName: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlChange = (url) => {
    handleChange('documentUrl', url);
    setPreviewUrl(url);
    if (url) {
      // Detect document type from URL
      if (url.toLowerCase().endsWith('.pdf') || url.includes('.pdf')) {
        handleChange('documentType', 'pdf');
      } else if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)/i)) {
        handleChange('documentType', 'image');
      }
    }
  };

  const handleSubmit = async () => {
    setUploadError('');
    
    let finalDocumentUrl = formData.documentUrl;
    let finalDocumentType = formData.documentType;
    let finalFileName = formData.fileName;

    // If file was uploaded, convert to base64
    if (selectedFile && uploadMethod === 1) {
      try {
        const base64 = await fileToBase64(selectedFile);
        finalDocumentUrl = base64;
        finalDocumentType = isImage(selectedFile) ? 'image' : 'pdf';
        finalFileName = selectedFile.name;
      } catch (error) {
        setUploadError('Error processing file. Please try again.');
        return;
      }
    }

    const submitData = {
      ...formData,
      eventDate: formData.eventDate.toDate(),
      documentUrl: finalDocumentUrl,
      documentType: finalDocumentType,
      fileName: finalFileName
    };
    
    onSubmit(submitData);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{event ? 'Edit Event' : 'Add New Event'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Event Date"
                  value={formData.eventDate}
                  onChange={(newValue) => handleChange('eventDate', newValue)}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  <MenuItem value="personal">Personal</MenuItem>
                  <MenuItem value="work">Work</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isAnniversary}
                    onChange={(e) => handleChange('isAnniversary', e.target.checked)}
                  />
                }
                label="Recurring Anniversary (shows yearly)"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                multiline
                rows={4}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  sx={{ flexGrow: 1 }}
                />
                <Button onClick={handleAddTag} variant="outlined" size="small">
                  Add
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Document (Image or PDF)
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Tabs value={uploadMethod} onChange={(e, newValue) => setUploadMethod(newValue)} sx={{ mb: 2 }}>
                  <Tab icon={<LinkIcon />} label="URL" />
                  <Tab icon={<UploadIcon />} label="Upload" />
                </Tabs>

                {uploadMethod === 0 && (
                  <Box>
                    <TextField
                      fullWidth
                      label="Document URL"
                      value={formData.documentUrl}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      placeholder="https://example.com/image.jpg or https://example.com/document.pdf"
                      helperText="Paste a URL to an image or PDF hosted elsewhere (e.g., Imgur, Google Drive, Dropbox, etc.)"
                    />
                    {previewUrl && formData.documentType === 'image' && !previewUrl.startsWith('data:') && (
                      <Box sx={{ mt: 2 }}>
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {uploadMethod === 1 && (
                  <Box>
                    <input
                      ref={fileInputRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="document-upload"
                      type="file"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="document-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                      >
                        {selectedFile ? 'Change Image' : 'Upload Image'}
                      </Button>
                    </label>
                    {selectedFile && (
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          icon={<ImageIcon />}
                          label={`${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} KB)`}
                          onDelete={handleRemoveFile}
                          color="primary"
                          sx={{ mb: 1 }}
                        />
                        {previewUrl && (
                          <Box>
                            <img 
                              src={previewUrl} 
                              alt="Preview" 
                              style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', marginTop: '8px' }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      Upload small images directly (max 1MB). For larger files or PDFs, use the URL method.
                    </Typography>
                  </Box>
                )}
              </Box>
              {uploadError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {uploadError}
                </Alert>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!formData.title}>
          {event ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventForm;

